// src/pages/AccountsPage.js
import React, { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
  Chip,
  IconButton,
  Drawer,
  BottomNavigation,
  BottomNavigationAction,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  alpha
} from '@mui/material';
import {
  Logout,
  AccountBalance,
  Receipt,
  Engineering,
  Handshake,
  Assessment,
  Build,
  NotificationsNone,
  Menu as MenuIcon,
  Home,
  TrendingUp,
  Payment,
  Person,
  Add,
  ArrowBack,
  Close
} from '@mui/icons-material';

const AccountsPage = () => {
  const { user, logout } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navValue, setNavValue] = useState(1);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({
    type: 'business',
    balance: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchAccounts();
  }, [user, navigate]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/accounts');
      setAccounts(data.accounts || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to fetch accounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleBottomNavChange = (event, newValue) => {
    setNavValue(newValue);
    switch(newValue) {
      case 0:
        navigate('/dashboard');
        break;
      case 1:
        navigate('/accounts');
        break;
      case 2:
        navigate('/projects');
        break;
      case 3:
        navigate('/payments');
        break;
      case 4:
        navigate('/profile');
        break;
      default:
        break;
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get user name
  const displayName = user?.name || 'User';
  const firstName = displayName.split(' ')[0];
  
  const getUserInitials = () => {
    return displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Generate a realistic-looking account number
  const generateAccountNumber = (id) => {
    const lastFour = id?.slice(-4) || '0000';
    const bankPrefix = '48';
    const routingPart = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${bankPrefix}${routingPart}${lastFour}`;
  };

  // Format account number with spaces every 4 digits
  const formatAccountNumber = (accountNumber) => {
    return accountNumber.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  // Handle Create Account
  const handleCreateAccount = async () => {
    const errors = {};
    if (!formData.type) errors.type = 'Account type is required';
    if (formData.balance && isNaN(formData.balance)) errors.balance = 'Balance must be a number';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const { data } = await API.post('/accounts', {
        type: formData.type,
        balance: parseFloat(formData.balance) || 0
      });
      
      setAccounts([...accounts, data.account]);
      setOpenCreateDialog(false);
      setFormData({ type: 'business', balance: '' });
      setFormErrors({});
      showSnackbar('Account created successfully');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create account', 'error');
    }
  };

  // Handle View Account
  const handleViewAccount = async (account) => {
    try {
      const { data } = await API.get(`/accounts/${account._id}`);
      setSelectedAccount(data.account);
      setOpenViewDialog(true);
    } catch (err) {
      showSnackbar('Failed to fetch account details', 'error');
    }
  };

  // Calculate totals
  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
  const businessAccounts = accounts.filter(acc => acc.type === 'business');
  const personalAccounts = accounts.filter(acc => acc.type === 'personal');
  const investmentAccounts = accounts.filter(acc => acc.type === 'investment');

  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#F9FAFB',
        p: 3
      }}>
        <Box sx={{ width: '100%', maxWidth: '300px', textAlign: 'center' }}>
          <LinearProgress 
            sx={{ 
              backgroundColor: alpha('#3FCFA1', 0.1), 
              '& .MuiLinearProgress-bar': { backgroundColor: '#3FCFA1' },
              mb: 2,
              borderRadius: 2,
              height: 6
            }} 
          />
          <Typography variant="body2" sx={{ color: '#64748B' }}>
            Loading your accounts...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh',
      width: '100vw',
      bgcolor: '#F9FAFB',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }}>
      {/* Mobile Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'white',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton 
            size="small" 
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#1A2B3C' }}
          >
            <ArrowBack />
          </IconButton>
          <Box
            component="img"
            src="/logo.png"
            alt="The Gantry Bank"
            sx={{ height: 32, width: 'auto' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" sx={{ color: '#64748B' }}>
            <NotificationsNone sx={{ fontSize: 20 }} />
          </IconButton>
          <Avatar 
            onClick={() => setMobileMenuOpen(true)}
            sx={{ 
              bgcolor: '#3FCFA1', 
              width: 32, 
              height: 32,
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {getUserInitials()}
          </Avatar>
        </Box>
      </Paper>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'white'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar sx={{ bgcolor: '#3FCFA1', width: 48, height: 48 }}>
              {getUserInitials()}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {displayName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Contractor Account
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <List>
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                handleNavigation('/dashboard');
              }}
              sx={{ 
                borderRadius: 2,
                mb: 0.5
              }}
            >
              <ListItemText 
                primary="Dashboard" 
                primaryTypographyProps={{
                  sx: { fontWeight: 400, color: '#1A2B3C' }
                }}
              />
            </ListItem>
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                handleNavigation('/accounts');
              }}
              sx={{ 
                borderRadius: 2,
                mb: 0.5,
                bgcolor: alpha('#3FCFA1', 0.1)
              }}
            >
              <ListItemText 
                primary="Accounts" 
                primaryTypographyProps={{
                  sx: { fontWeight: 600, color: '#3FCFA1' }
                }}
              />
            </ListItem>
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                handleNavigation('/projects');
              }}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemText primary="Projects" />
            </ListItem>
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                handleNavigation('/bids');
              }}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemText primary="Bids" />
            </ListItem>
            <ListItem 
              button 
              onClick={() => {
                setMobileMenuOpen(false);
                handleNavigation('/equipment');
              }}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemText primary="Equipment" />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              borderColor: '#E2E8F0',
              color: '#64748B',
              textTransform: 'none',
              justifyContent: 'flex-start',
              '&:hover': {
                borderColor: '#3FCFA1',
                color: '#3FCFA1'
              }
            }}
          >
            Logout
          </Button>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        px: 2,
        py: 2,
        pb: 8
      }}>
        {/* Header with Create Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
              Your Accounts
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Manage all your business accounts
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{
              bgcolor: '#3FCFA1',
              '&:hover': { bgcolor: '#2DAF81' },
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
              py: 1,
              fontSize: '0.8rem'
            }}
          >
            New
          </Button>
        </Box>

        {/* Summary Cards - Perfectly aligned with account cards */}
        <Box sx={{ mb: 3 }}>
          {/* Total Balance and Total Accounts - Side by side, each half width */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Paper 
                sx={{ 
                  p: 2,
                  bgcolor: alpha('#3FCFA1', 0.05),
                  border: '1px solid',
                  borderColor: alpha('#3FCFA1', 0.2),
                  borderRadius: 2,
                  height: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
                  Total Balance
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C', fontSize: '1.1rem' }}>
                  {formatCurrency(totalBalance)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper 
                sx={{ 
                  p: 2,
                  border: '1px solid',
                  borderColor: '#E2E8F0',
                  borderRadius: 2,
                  height: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
                  Total Accounts
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C', fontSize: '1.1rem' }}>
                  {accounts.length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Account Type Tabs - Three equal columns */}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center',
                  bgcolor: businessAccounts.length > 0 ? alpha('#3FCFA1', 0.1) : 'transparent',
                  border: '1px solid',
                  borderColor: businessAccounts.length > 0 ? alpha('#3FCFA1', 0.3) : '#E2E8F0',
                  borderRadius: 2,
                  height: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
                  Business
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: businessAccounts.length > 0 ? '#3FCFA1' : '#1A2B3C' }}>
                  {businessAccounts.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center',
                  bgcolor: personalAccounts.length > 0 ? alpha('#FFB547', 0.1) : 'transparent',
                  border: '1px solid',
                  borderColor: personalAccounts.length > 0 ? alpha('#FFB547', 0.3) : '#E2E8F0',
                  borderRadius: 2,
                  height: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
                  Personal
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: personalAccounts.length > 0 ? '#FFB547' : '#1A2B3C' }}>
                  {personalAccounts.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center',
                  bgcolor: investmentAccounts.length > 0 ? alpha('#1E3A8A', 0.1) : 'transparent',
                  border: '1px solid',
                  borderColor: investmentAccounts.length > 0 ? alpha('#1E3A8A', 0.3) : '#E2E8F0',
                  borderRadius: 2,
                  height: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
                  Investment
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: investmentAccounts.length > 0 ? '#1E3A8A' : '#1A2B3C' }}>
                  {investmentAccounts.length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Accounts List */}
        {accounts.length === 0 ? (
          <Card sx={{ 
            borderRadius: 3, 
            textAlign: 'center', 
            py: 6,
            px: 3,
            bgcolor: 'white'
          }}>
            <AccountBalance sx={{ fontSize: 60, color: '#CBD5E1', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#1A2B3C', mb: 1 }}>
              No accounts yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Create your first account to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenCreateDialog(true)}
              sx={{
                bgcolor: '#3FCFA1',
                '&:hover': { bgcolor: '#2DAF81' },
                textTransform: 'none'
              }}
            >
              Create Account
            </Button>
          </Card>
        ) : (
          <List sx={{ mt: 1 }}>
            {accounts.map((account) => {
              const accountNumber = generateAccountNumber(account._id);
              return (
                <Card 
                  key={account._id} 
                  sx={{ 
                    borderRadius: 3, 
                    mb: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(63, 207, 161, 0.15)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleViewAccount(account)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ 
                          bgcolor: account.type === 'business' ? alpha('#3FCFA1', 0.1) :
                                  account.type === 'personal' ? alpha('#FFB547', 0.1) : alpha('#1E3A8A', 0.1),
                          color: account.type === 'business' ? '#3FCFA1' :
                                 account.type === 'personal' ? '#FFB547' : '#1E3A8A'
                        }}>
                          <AccountBalance />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {account.type} Account
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
                            {formatAccountNumber(accountNumber)}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label={account.type}
                        size="small"
                        sx={{ 
                          bgcolor: account.type === 'business' ? alpha('#3FCFA1', 0.1) :
                                  account.type === 'personal' ? alpha('#FFB547', 0.1) : alpha('#1E3A8A', 0.1),
                          color: account.type === 'business' ? '#3FCFA1' :
                                 account.type === 'personal' ? '#FFB547' : '#1E3A8A',
                          textTransform: 'capitalize',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
                        Current Balance
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                        {formatCurrency(account.balance)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </List>
        )}
      </Box>

      {/* Bottom Navigation */}
      <Paper 
        elevation={3} 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          borderRadius: 0,
          borderTop: '1px solid',
          borderColor: 'grey.100'
        }}
      >
        <BottomNavigation
          showLabels
          value={navValue}
          onChange={handleBottomNavChange}
          sx={{
            height: 56,
            '& .Mui-selected': {
              color: '#3FCFA1 !important'
            }
          }}
        >
          <BottomNavigationAction label="Home" icon={<Home />} />
          <BottomNavigationAction label="Accounts" icon={<AccountBalance />} />
          <BottomNavigationAction label="Projects" icon={<Engineering />} />
          <BottomNavigationAction label="Payments" icon={<Payment />} />
          <BottomNavigationAction label="Profile" icon={<Person />} />
        </BottomNavigation>
      </Paper>

      {/* Create Account Dialog */}
      <Dialog 
        open={openCreateDialog} 
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Create New Account
            </Typography>
            <IconButton onClick={() => setOpenCreateDialog(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Account Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              error={!!formErrors.type}
              helperText={formErrors.type}
              margin="normal"
              size="small"
            >
              <MenuItem value="business">Business Account</MenuItem>
              <MenuItem value="personal">Personal Account</MenuItem>
              <MenuItem value="investment">Investment Account</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Initial Deposit (Optional)"
              type="number"
              value={formData.balance}
              onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
              error={!!formErrors.balance}
              helperText={formErrors.balance}
              margin="normal"
              size="small"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1, color: '#64748B' }}>$</Typography>
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setOpenCreateDialog(false)} sx={{ color: '#64748B', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateAccount}
            sx={{ bgcolor: '#3FCFA1', '&:hover': { bgcolor: '#2DAF81' }, textTransform: 'none' }}
          >
            Create Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Account Dialog */}
      <Dialog 
        open={openViewDialog} 
        onClose={() => setOpenViewDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedAccount && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {selectedAccount.type} Account Details
                </Typography>
                <IconButton onClick={() => setOpenViewDialog(false)} size="small">
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Card sx={{ bgcolor: alpha('#3FCFA1', 0.05), borderRadius: 3, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: selectedAccount.type === 'business' ? '#3FCFA1' :
                                selectedAccount.type === 'personal' ? '#FFB547' : '#1E3A8A',
                        width: 48,
                        height: 48
                      }}>
                        <AccountBalance />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                          {selectedAccount.type} Account
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
                          {formatAccountNumber(generateAccountNumber(selectedAccount._id))}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
                          Current Balance
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                          {formatCurrency(selectedAccount.balance)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Quick Actions
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Payment />}
                      sx={{
                        py: 1,
                        borderColor: '#E2E8F0',
                        color: '#1A2B3C',
                        textTransform: 'none',
                        '&:hover': { borderColor: '#3FCFA1' }
                      }}
                    >
                      Transfer
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Receipt />}
                      sx={{
                        py: 1,
                        borderColor: '#E2E8F0',
                        color: '#1A2B3C',
                        textTransform: 'none',
                        '&:hover': { borderColor: '#3FCFA1' }
                      }}
                    >
                      Statement
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button 
                onClick={() => setOpenViewDialog(false)}
                fullWidth
                sx={{
                  bgcolor: '#3FCFA1',
                  color: 'white',
                  '&:hover': { bgcolor: '#2DAF81' },
                  textTransform: 'none',
                  py: 1.5
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            borderRadius: 2,
            width: '100%',
            bgcolor: snackbar.severity === 'success' ? alpha('#3FCFA1', 0.1) : alpha('#EF4444', 0.1),
            color: snackbar.severity === 'success' ? '#3FCFA1' : '#EF4444'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountsPage;